import React, { useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getUnprocessedHoldingQueue } from '../../helper/Api';
import { AddCommasToAmount } from '../../helper/Utils';
import './SettlementQueueList.css';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 72;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_CARD_PADDING = 32;
const TABLE_BOTTOM_GAP = 16;

const CenterCell = ({ children }) => (
    <div className="settlement-queue-cell-center">{children}</div>
);

const formatDate = (value) => {
    if (!value) return '';
    return moment(value, [
        'YYYY-MM-DD',
        'DD-MM-YYYY',
        'YYYY-MM-DDTHH:mm:ssZ',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
        moment.ISO_8601,
    ]).format('DD-MM-YYYY');
};

const formatAmountType = (value) => {
    if (!value) return '';
    return String(value)
        .split('_')
        .map((part) => part.toUpperCase())
        .join(' ');
};

const getSourceMeta = (row) => {
    if (row.invoice_id) {
        return {
            type: 'Invoice',
            primaryLabel: 'Invoice No',
            orderNo: row.invoice_no,
            orderDate: row.invoice_date,
            secondaryLabel: 'Voucher No',
            secondaryNo: row.voucher_order_no,
            secondaryDate: row.voucher_order_date,
        };
    }
    if (row.salary_id) {
        return {
            type: 'Salary',
            primaryLabel: 'Order No',
            orderNo: row.salary_order_no,
            orderDate: row.salary_order_date,
        };
    }
    if (row.beneficiary_reimbursement_id) {
        return {
            type: 'Reimbursement',
            primaryLabel: 'Order No',
            orderNo: row.beneficiary_reimbursement_order_no,
            orderDate: row.beneficiary_reimbursement_order_date,
        };
    }
    return { type: 'Unknown', primaryLabel: 'Order No', orderNo: '', orderDate: '' };
};

const getProcessBadgeClass = (isProcessed) => {
    if (Number(isProcessed) === 1) return 'bg-success-20';
    return 'bg-info-20';
};

const getProcessIconClass = (isProcessed) => {
    if (Number(isProcessed) === 1) return 'icon-verified text-success-100';
    return 'icon-queue text-info-100';
};

const getProcessLabel = (isProcessed) => {
    if (Number(isProcessed) === 1) return 'Processed';
    return 'In Queue';
};

const SettlementQueueList = () => {
    const tableContainerRef = useRef(null);
    const { authData } = useSelector((state) => state.authData);
    const departmentId = authData?.user?.department_id;
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');

    const fetchQueue = async () => {
        try {
            setLoading(true);
            const response = await getUnprocessedHoldingQueue({
                department_id: departmentId,
            });
            if (response.data.status) {
                setData(response.data.result || []);
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQueue();
    }, [departmentId]);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.min(data.length || 1, rowsPerPage);
            const bodyContentHeight = rowCount * TABLE_ROW_HEIGHT;
            const containerTop = tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight = window.innerHeight
                - containerTop
                - TABLE_HEADER_HEIGHT
                - TABLE_PAGINATION_HEIGHT
                - TABLE_CARD_PADDING
                - TABLE_BOTTOM_GAP;
            const nextHeight = Math.min(
                bodyContentHeight,
                Math.max(maxBodyHeight, 120)
            );
            setTableScrollHeight(`${nextHeight}px`);
        };

        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, [data.length, rowsPerPage, loading]);

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'clip',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const columns = [
        {
            name: 'S.No',
            cell: (_row, index) => (
                <CenterCell>{(currentPage - 1) * rowsPerPage + index + 1}</CenterCell>
            ),
            center: true,
            width: '70px',
            grow: 0,
        },
        {
            name: 'Source Type',
            cell: (row) => {
                const source = getSourceMeta(row);
                return (
                    <CenterCell>
                        <span className="settlement-queue-source-type">{source.type}</span>
                    </CenterCell>
                );
            },
            center: true,
            width: '120px',
            grow: 0,
        },
        {
            name: 'Source Details',
            cell: (row) => {
                const source = getSourceMeta(row);
                return (
                    <div>
                        <div><b>{source.orderNo || '-'}</b></div>
                        {source.orderDate && (
                            <div className="text-muted fs-12px">{formatDate(source.orderDate)}</div>
                        )}
                        {source.secondaryNo && (
                            <>
                                <div className="text-muted fs-12px mt-1">
                                    <b>{source.secondaryLabel}:</b> {source.secondaryNo}
                                </div>
                                {source.secondaryDate && (
                                    <div className="text-muted fs-12px">
                                        {formatDate(source.secondaryDate)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            },
            wrap: true,
            grow: 1.2,
            minWidth: '120px',
        },
        {
            name: 'Amount Type',
            cell: (row) => (
                <CenterCell>{formatAmountType(row.amount_type)}</CenterCell>
            ),
            center: true,
            wrap: true,
            grow: 1,
            minWidth: '110px',
        },
        {
            name: 'Amount',
            cell: (row) => (
                <CenterCell>
                    {row.amount !== null && row.amount !== undefined && row.amount !== ''
                        ? AddCommasToAmount(row.amount)
                        : ''}
                </CenterCell>
            ),
            center: true,
            grow: 1,
            minWidth: '100px',
        },
        {
            name: 'Debit Account',
            cell: (row) => (
                <CenterCell>
                    <b>{row.debit_account_no || '-'}</b>
                </CenterCell>
            ),
            center: true,
            wrap: true,
            grow: 1.2,
            minWidth: '140px',
        },
        {
            name: 'Status',
            cell: (row) => (
                <CenterCell>
                    <span
                        className={`settlement-queue-status-badge ${getProcessBadgeClass(row.is_processed)}`}
                    >
                        {getProcessLabel(row.is_processed)}
                        <i className={`${getProcessIconClass(row.is_processed)} fs-16px`} />
                    </span>
                </CenterCell>
            ),
            center: true,
            width: '120px',
            grow: 0,
        },
        {
            name: 'Created Date',
            cell: (row) => (
                <CenterCell>{formatDate(row.created_at) || '-'}</CenterCell>
            ),
            center: true,
            width: '120px',
            grow: 0,
        },
    ];

    return (
        <div ref={tableContainerRef} className="settlement-queue-datatable-root card p-3">
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={data}
                progressPending={loading}
                fixedHeader
                fixedHeaderScrollHeight={tableScrollHeight}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={(newRowsPerPage) => {
                    setRowsPerPage(newRowsPerPage);
                    setCurrentPage(1);
                }}
                noDataComponent={
                    <div className="p-4 text-muted">No unprocessed holding queue records found.</div>
                }
            />
        </div>
    );
};

export default SettlementQueueList;
